// TypeScript Types für das Lead-Management-System

export interface Customer {
  id: string;
  name: string;
  slug: string;
  forward_email: string;
  forward_cc: string[];
  /** Persoenliche Adresse fuer Monatsbericht-Mails (Migration 044) */
  report_email?: string | null;
  /** Software-/Plattformkunde ohne Marketing-Projekte (Migration 046) */
  software_only?: boolean;
  /** Erzeugt der Monats-Cron automatisch einen Berichtsentwurf? (Migration 050) */
  auto_report?: boolean;
  /** Nach Publivo umgezogen (Migration 100): Portal zeigt nur noch den Umzugs-Hinweis */
  publivo_umgezogen_am?: string | null;
  active: boolean;
  created_at: string;
  logo_url?: string;
  brand_color?: string;
  // Stripe-Abrechnung (Migration 019)
  subscription_status?: SubscriptionStatus;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  stripe_price_id?: string | null;
  stripe_current_period_end?: string | null;
}

// Abo-Status (von Stripe-Webhook synchronisiert)
export type SubscriptionStatus = 'none' | 'active' | 'past_due' | 'canceled' | 'expired';

export const subscriptionStatusLabels: Record<SubscriptionStatus, { label: string; bg: string; text: string }> = {
  none: { label: 'Kein Abo', bg: 'bg-gray-100', text: 'text-gray-600' },
  active: { label: 'Aktiv', bg: 'bg-green-100', text: 'text-green-800' },
  past_due: { label: 'Zahlung offen', bg: 'bg-amber-100', text: 'text-amber-800' },
  canceled: { label: 'Gekündigt', bg: 'bg-red-100', text: 'text-red-800' },
  expired: { label: 'Abgelaufen', bg: 'bg-gray-100', text: 'text-gray-600' },
};

export type LeadStatus = 'new' | 'contacted' | 'in_progress' | 'converted' | 'archived';

export interface Lead {
  id: string;
  customer_id: string;
  customer_slug: string;
  project_id?: string | null;
  form_identifier: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  fields: Record<string, any>;
  field_labels: Record<string, string>;
  files?: LeadFile[];
  status: LeadStatus;
  outcome?: 'won' | 'lost' | 'spam' | null; // was aus dem Lead wurde (Lead-Qualitäts-Schleife)
  // Grund, wenn der Spamfilter (Migration 094) den Lead beim Speichern
  // abgefangen hat; bei von Hand markiertem Spam leer.
  spam_grund?: string | null;
  forwarded_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  customer?: Customer;
  project?: Project;
}

export interface LeadFile {
  field_name: string;
  file_name: string;
  file_url: string;
}

export interface FormStatistic {
  id: string;
  customer_slug: string;
  form_identifier: string;
  submission_count: number;
  last_submission: string;
  created_at: string;
  updated_at: string;
}

// Dashboard Statistics
export interface DashboardStats {
  total_leads: number;
  new_leads: number;
  today_leads: number;
  this_week_leads: number;
}

export interface CustomerStats {
  customer_id: string;
  customer_name: string;
  customer_slug: string;
  total_leads: number;
  new_leads: number;
  today_leads: number;
  this_week_leads: number;
}

// Filter & Pagination
export interface LeadFilters {
  customer_slug?: string;
  project_id?: string;
  form_identifier?: string;
  status?: LeadStatus;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface PaginationParams {
  page: number;
  per_page: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Auth
export interface User {
  id: string;
  email: string;
  created_at: string;
  role?: UserRole;
  customer_id?: string | null;
  customer?: Customer | null;
}

// „vertrieb" (Migration 073, Nico 06.09.2026): Mitarbeiter, der die Anrufliste
// abarbeitet — sieht nur Betriebe, CRM und To-Dos, keine Kunden-/Finanzdaten.
export type UserRole = 'admin' | 'customer' | 'vertrieb';

export interface UserProfile {
  id: string;
  role: UserRole;
  customer_id: string | null;
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

// API Responses
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Form Data
export interface CustomerFormData {
  name: string;
  slug: string;
  forward_email: string;
  forward_cc: string[];
  report_email?: string | null;
  software_only?: boolean;
  auto_report?: boolean;
  active: boolean;
  logo_url?: string;
  brand_color?: string;
  stripe_price_id?: string | null;
}

// Projekte (Formulare pro Kunde)
/**
 * Projektart (Migration 043). Bestimmt, was das System vom Projekt ERWARTET:
 * welche Berichtsabschnitte entstehen, welche Wortwahl gilt (Anfragen vs.
 * Bewerbungen) und welche fehlenden Datenquellen als Luecke gemeldet werden.
 */
export type ProjectType = 'landingpage' | 'website' | 'bewerbungen';

export interface Project {
  id: string;
  customer_id: string;
  name: string;
  slug: string;
  project_type: ProjectType;
  description?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  gsc_property?: string | null;
  ads_account_id?: string | null;
  publivo_org_id?: string | null;
  business_context?: string | null;
  customer?: Customer;
}

export interface ProjectFormData {
  customer_id: string;
  name: string;
  slug: string;
  /** '' nur im Formular, bis gewaehlt — beim Absenden Pflicht */
  project_type: ProjectType | '';
  description?: string;
  active: boolean;
  gsc_property?: string | null;
  ads_account_id?: string | null;
  business_context?: string | null;
}

export interface LeadUpdateData {
  status?: LeadStatus;
  notes?: string;
  outcome?: 'won' | 'lost' | 'spam' | null;
}

// Form Submissions
export type FormType = 'lead_generation' | 'recruiting';
export type SubmissionStatus = 'new' | 'processing' | 'completed' | 'archived';

export interface FormSubmission {
  id: string;
  customer_id: string;
  project_id?: string | null;
  form_type: FormType;
  form_data: Record<string, any>;
  field_order?: string[] | null;
  status: SubmissionStatus;
  submitted_at: string;
  processed_at?: string | null;
  notes?: string | null;
  tags?: string[];
  created_at: string;
  updated_at: string;
  // Joined data
  customer_name?: string;
  customer_slug?: string;
  project_name?: string;
  project_slug?: string;
  file_count?: number;
  files?: FormSubmissionFile[];
}

export interface FormSubmissionFile {
  id: string;
  submission_id: string;
  field_name: string;
  original_filename: string;
  storage_path: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
  download_url?: string;
}

export interface FormSubmissionCreateData {
  customer_slug: string;
  project_slug?: string;
  form_type: FormType;
  form_data: Record<string, any>;
}

// Status Badge Colors
export const statusColors: Record<LeadStatus, { bg: string; text: string; label: string }> = {
  new: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Neu' },
  contacted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Kontaktiert' },
  in_progress: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'In Bearbeitung' },
  converted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Konvertiert' },
  archived: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Archiviert' },
};

export const submissionStatusColors: Record<SubmissionStatus, { bg: string; text: string; label: string }> = {
  new: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Neu' },
  processing: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'In Bearbeitung' },
  completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Abgeschlossen' },
  archived: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Archiviert' },
};

export const formTypeLabels: Record<FormType, string> = {
  lead_generation: 'Lead-Generierung',
  recruiting: 'Recruiting',
};

// ============================================
// CRM & Task Management Types
// ============================================

// Deal Stage
export type DealStage = 
  | 'lead'          // Initiale Anfrage
  | 'contact'       // Erstkontakt hergestellt
  | 'qualified'     // Qualifiziert
  | 'proposal'      // Angebot erstellt
  | 'negotiation'   // In Verhandlung
  | 'won'          // Gewonnen (wird zu Kunde)
  | 'lost';        // Verloren

// Service Type
export type ServiceType = 'lead_generation' | 'recruiting' | 'both';

// Deal (Akquise/Opportunity)
export interface Deal {
  id: string;
  title: string;
  description: string | null;
  value: number | null;
  stage: DealStage;
  probability: number;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  service_type: ServiceType | null;
  customer_id: string | null;
  expected_close_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  won_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  tags: string[] | null;
  // Joined data
  customer_company_name?: string;
  task_count?: number;
  completed_task_count?: number;
  overdue_task_count?: number;
}

// Deal Activity
export interface DealActivity {
  id: string;
  deal_id: string;
  activity_type: 'stage_change' | 'note' | 'call' | 'email' | 'meeting';
  description: string | null;
  old_stage: DealStage | null;
  new_stage: DealStage | null;
  created_at: string;
}

// Task Priority
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

// Task Status
export type TaskStatus = 'todo' | 'in_progress' | 'completed' | 'cancelled';

// Checklist Item
export interface ChecklistItem {
  text: string;
  completed: boolean;
}

// Task
export interface Task {
  id: string;
  title: string;
  description: string | null;
  deal_id: string | null;
  customer_id: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string | null;
  completed_at: string | null;
  checklist: ChecklistItem[] | null;
  notes: string | null;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  template_id: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  customer_company_name?: string;
  deal_title?: string;
  is_overdue?: boolean;
}

// Task Template Item
export interface TaskTemplateItem {
  title: string;
  description?: string;
  due_days: number;
  priority: TaskPriority;
  checklist?: ChecklistItem[];
}

// Task Template
export interface TaskTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  tasks: TaskTemplateItem[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Deal Create/Update Data
export interface DealFormData {
  title: string;
  description?: string;
  value?: number;
  stage?: DealStage;
  probability?: number;
  company_name?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  service_type?: ServiceType;
  customer_id?: string;
  expected_close_date?: string;
  notes?: string;
  tags?: string[];
}

// Task Create/Update Data
export interface TaskFormData {
  title: string;
  description?: string;
  deal_id?: string;
  customer_id?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  due_date?: string;
  checklist?: ChecklistItem[];
  notes?: string;
  is_recurring?: boolean;
  recurrence_pattern?: string;
}

// Filters
export interface DealFilters {
  stage?: DealStage | 'all';
  service_type?: ServiceType | 'all';
  search?: string;
}

export interface TaskFilters {
  status?: TaskStatus | 'all';
  priority?: TaskPriority | 'all';
  assigned_to?: 'deals' | 'customers' | 'all';
  overdue?: boolean;
  search?: string;
}

// Label/Color mappings
export const dealStageLabels: Record<DealStage, string> = {
  lead: 'Anfrage',
  contact: 'Kontakt',
  qualified: 'Qualifiziert',
  proposal: 'Angebot',
  negotiation: 'Verhandlung',
  won: 'Gewonnen',
  lost: 'Verloren',
};

export const dealStageColors: Record<DealStage, { bg: string; text: string }> = {
  lead: { bg: 'bg-gray-100', text: 'text-gray-800' },
  contact: { bg: 'bg-blue-100', text: 'text-blue-800' },
  qualified: { bg: 'bg-cyan-100', text: 'text-cyan-800' },
  proposal: { bg: 'bg-purple-100', text: 'text-purple-800' },
  negotiation: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  won: { bg: 'bg-green-100', text: 'text-green-800' },
  lost: { bg: 'bg-red-100', text: 'text-red-800' },
};

export const taskStatusLabels: Record<TaskStatus, string> = {
  todo: 'Offen',
  in_progress: 'In Bearbeitung',
  completed: 'Erledigt',
  cancelled: 'Abgebrochen',
};

export const taskStatusColors: Record<TaskStatus, { bg: string; text: string }> = {
  todo: { bg: 'bg-gray-100', text: 'text-gray-800' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-800' },
  completed: { bg: 'bg-green-100', text: 'text-green-800' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
};

export const taskPriorityLabels: Record<TaskPriority, string> = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  urgent: 'Dringend',
};

export const taskPriorityColors: Record<TaskPriority, { bg: string; text: string }> = {
  low: { bg: 'bg-gray-100', text: 'text-gray-600' },
  medium: { bg: 'bg-blue-100', text: 'text-blue-600' },
  high: { bg: 'bg-orange-100', text: 'text-orange-600' },
  urgent: { bg: 'bg-red-100', text: 'text-red-600' },
};

export const serviceTypeLabels: Record<ServiceType, string> = {
  lead_generation: 'Lead-Generierung',
  recruiting: 'Recruiting',
  both: 'Beides',
};
